<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-5 sm:p-6 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center border-b border-gray-700 pb-3">
        <div class="flex items-center gap-3">
          <i class="fas fa-swatchbook text-indigo-300 text-2xl"></i>
          <h2 class="text-lg sm:text-xl font-semibold text-indigo-300">ลายสักของฉัน</h2>
        </div>
        <button
          @click="closeModal"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200"
          :disabled="loading"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base"></i>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        <p class="text-indigo-200 text-sm mt-3">กำลังโหลด...</p>
      </div>

      <!-- Controls -->
      <div v-else class="space-y-3">
        <!-- Filters -->
        <div class="bg-gray-800 border border-gray-700 rounded-md p-2 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
          <button
            v-for="f in filters"
            :key="f.value"
            @click="selectFilter(f.value)"
            :class="[
              'px-3 py-2 rounded-md text-xs sm:text-sm font-semibold transition-colors duration-200 text-center border',
              selectedFilter === f.value
                ? 'bg-indigo-600 text-white border-indigo-700'
                : 'bg-gray-700 text-indigo-200 hover:bg-gray-600 border-gray-600'
            ]"
            :aria-selected="selectedFilter === f.value"
          >
            {{ f.label }}
          </button>
        </div>
      </div>

      <!-- Grid -->
      <div v-if="!loading" class="rounded-md overflow-hidden">
        <div
          v-if="tattoos.length > 0"
          class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto"
          ref="tattooList"
          id="style-1"
        >
          <div
            v-for="tattoo in tattoos"
            :key="tattoo.id"
            class="p-3 rounded-md text-sm transition-all duration-200 flex flex-col items-center relative border w-full
                   bg-gray-800 bg-opacity-80 border-gray-700 hover:bg-gray-700 hover:border-indigo-500"
          >
            <img
              :src="tattoo.imageUrl || fallbackImg"
              alt="Tattoo"
              class="w-28 h-28 sm:w-32 sm:h-32 rounded-md object-cover mb-2 mx-auto border border-gray-700"
              @error="handleImageError($event)"
            />
            <span class="font-semibold text-center text-indigo-200 mb-2 block leading-tight">
              {{ tattoo.name }}
            </span>

            <div class="flex flex-col items-center gap-1 text-xs text-indigo-200">
              <span v-if="tattoo.stats?.str > 0">STR +{{ tattoo.stats.str }}</span>
              <span v-if="tattoo.stats?.stamina > 0">STAMINA +{{ tattoo.stats.stamina }}</span>
              <span v-if="tattoo.stats?.int > 0">INT +{{ tattoo.stats.int }}</span>
              <span v-if="tattoo.stats?.agi > 0">AGI +{{ tattoo.stats.agi }}</span>
              <span v-if="tattoo.stats?.luck > 0">LUCK +{{ tattoo.stats.luck }}</span>
            </div>

            <div class="h-px w-12 bg-gray-700 my-2"></div>

            <div class="flex flex-col items-center gap-1 text-xs">
              <span v-if="tattoo.cost?.silver > 0" class="flex items-center gap-1 text-indigo-200">
                {{ formatNumber(tattoo.cost.silver) }} <i class="fas fa-coins text-gray-300 text-2xs"></i>
              </span>
              <span v-if="tattoo.cost?.gold > 0" class="flex items-center gap-1 text-indigo-200">
                {{ formatNumber(tattoo.cost.gold) }} <i class="fas fa-g text-yellow-500 text-2xs"></i>
              </span>
              <span v-if="tattoo.cost?.diamond > 0" class="flex items-center gap-1 text-indigo-200">
                {{ formatNumber(tattoo.cost.diamond) }} <i class="fas fa-gem text-blue-500 text-2xs"></i>
              </span>
            </div>

            <span
              v-if="tattooNames.includes(tattoo.name)"
              class="absolute top-2 right-2 text-green-400"
              title="สักแล้ว"
            >
              <i class="fas fa-check-circle"></i>
            </span>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-indigo-200 text-sm text-center py-8 border border-dashed border-gray-700 rounded-md">
          ไม่พบลายสักในหมวดนี้
        </div>
      </div>

      <!-- Error -->
      <div v-if="errorMessage" class="text-red-300 text-sm text-center">
        {{ errorMessage }}
      </div>

      <!-- Footer -->
      <div class="pt-2 text-right text-xs text-indigo-200">
        รวม <span class="text-white font-semibold">{{ tattoos.length }}</span> ลายในหมวด <span class="text-white font-semibold">{{ currentLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { projectFirestore } from '@/firebase/config';

export default {
  name: 'TattooModal',
  props: { isOpen: { type: Boolean, default: false } },
  emits: ['close'],
  setup(props, { emit }) {
    const playerStore = usePlayerStore();
    const tattoos = ref([]);
    const tattooNames = computed(() => playerStore.characterPrivateData?.tattooNames || []);
    const errorMessage = ref('');
    const loading = ref(true);
    const selectedFilter = ref('str');
    const fallbackImg = 'https://via.placeholder.com/150';

    const filters = ref([
      { value: 'str', label: 'STR' },
      { value: 'stamina', label: 'Stamina' },
      { value: 'int', label: 'INT' },
      { value: 'agi', label: 'AGI' },
      { value: 'luck', label: 'Luck' }
    ]);

    const currentLabel = computed(() => {
      const f = filters.value.find(x => x.value === selectedFilter.value);
      return f ? f.label : '';
    });

    const formatNumber = (num) => {
      const n = Number(num);
      if (!Number.isFinite(n)) return '0';
      try { return n.toLocaleString('th-TH'); } catch { return String(n); }
    };

    const handleImageError = (e) => { e.target.src = fallbackImg; };

    const fetchTattoos = async () => {
      try {
        loading.value = true;
        const snap = await projectFirestore
          .collection('tattoos')
          .where('type', '==', selectedFilter.value)
          .orderBy('order', 'asc')
          .get();
        tattoos.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Fetch tattoos error:', err);
        errorMessage.value = 'เกิดข้อผิดพลาดในการโหลดลายสัก';
      } finally {
        loading.value = false;
      }
    };

    const fetchUserOwned = async () => {
      await fetchTattoos();
    };

    const selectFilter = async (val) => {
      selectedFilter.value = val;
      await fetchTattoos();
    };

    const closeModal = () => emit('close');

    watch(() => props.isOpen, (open) => {
      if (open) fetchUserOwned();
    });

    onMounted(() => {
      if (props.isOpen) fetchUserOwned();
    });

    return {
      // state
      tattoos, tattooNames, errorMessage, loading, selectedFilter, filters, fallbackImg,
      // computed
      currentLabel,
      // methods
      formatNumber, handleImageError, selectFilter, closeModal
    };
  }
};
</script>

<style scoped>
/* Animations (Tailwind v2 compatible) */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

/* Spinner fallback */
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* Scrollbar (match your theme) */
#style-1 { scrollbar-width: thin; scrollbar-color: #4b5563 #1f2937; }
#style-1::-webkit-scrollbar { width: 6px; }
#style-1::-webkit-scrollbar-track { background: #1f2937; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #4b5563; }

/* Tiny util */
.text-2xs { font-size: 0.7rem; }
</style>
